import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Check,
  Pencil,
  RefreshCw,
  Trash,
  Unlink,
  UserPlus,
  X,
} from "@tamagui/lucide-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type RowData,
  type SortingState,
} from "@tanstack/react-table";
import {
  useAuth,
  useCustomers,
  useFirmwareUpdate,
  useHomeownersWithHubsListView,
  useLatestVersions,
  useHubs,
  useUserHubAssociation,
  useUserRole,
  useWebHubUpdate,
  Customer,
  Hub,
  HubInstallProgressConfig,
  HubInstallStatusConfig,
  HubMigrationEntry,
  HubMigrationsConfig,
  HubStatus,
  LatestVersions,
  UserRole,
  getHubPresence,
  isGatewayHub,
  useGatewayLink,
} from "common";
import { TFunction } from "i18next";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import {
  YStack,
  XStack,
  Text,
  Spinner,
  Card,
  Separator,
  ScrollView,
  Button,
  View,
  Dialog,
  Input,
  ViewProps,
  useMedia,
} from "tamagui";
import { Table } from "tamagui-config";

import DemoLauncher from "./DemoLauncher";
import StationFormModal, {
  FormValues as CreateHubFormValues,
} from "./StationFormModal";
import ConfirmDialog from "../../components/ConfirmDialog";
import { Select } from "../../components/Select";
import { TableCards } from "../../components/TableCards";
import { useShowToast } from "../../hooks/useShowToast";
import { iconLabel } from "../../utils/a11y";
import { formatDate, formatDateTime } from "../../utils/formatDate";
import { uploadToS3 } from "../../utils/s3Upload";
import { setDocumentTitle } from "../../utils/setDocumentTitle";

declare module "@tanstack/react-table" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    /** Relative column width; the table divides its row by the sum. */
    flex?: number;
    /** Narrowest the column may get, in px, before the table scrolls. */
    minWidth?: number;
  }
}

// Fixed-width cells (the Table default) either clip the action buttons or
// waste space on short serials, so every cell shares the row by flex weight,
// down to a floor that fits its widest value (measured: "SN00005702AD9D",
// the Maintenance pill, the install card, 2x2 action buttons).
const cellLayout = (column: {
  columnDef: { meta?: { flex?: number; minWidth?: number } };
}) => ({
  width: "auto" as const,
  flexGrow: column.columnDef.meta?.flex ?? 1,
  flexBasis: 0,
  minWidth: column.columnDef.meta?.minWidth ?? 0,
});

const STATUS_FILTERS = ["all", "ACTIVE", "PENDING", "INACTIVE"] as const;
const STATUS_RANK: Record<string, number> = { ACTIVE: 0, PENDING: 1, INACTIVE: 2 };
const statusRank = (status?: string | null) =>
  STATUS_RANK[(status ?? "").toUpperCase()] ?? 3;

interface LocationData {
  street?: string;
  city?: string;
  zip?: string;
  state?: string;
}

const formatLocation = (
  location: LocationData | string | null | undefined,
): string => {
  if (!location) return "";

  if (typeof location === "string") return location;

  const parts: string[] = [];

  if (location.street) parts.push(location.street);
  if (location.city) parts.push(location.city);
  if (location.state) parts.push(location.state);
  if (location.zip) parts.push(location.zip);

  return parts.join(", ");
};

const statusTheme = (status: string): string => {
  switch (status?.toLowerCase()) {
    case "active":
      return "green";
    case "inactive":
      return "red";
    case "maintenance":
      return "orange";
    case "pending":
      return "yellow";
    default:
      return "gray";
  }
};

const StatusIndicator = ({
  status,
  theme = statusTheme(status),
  ...rest
}: ViewProps & {
  status: string;
  theme?: string;
}) => (
  <View
    flexDirection="row"
    alignItems="center"
    gap="$2"
    theme={theme as never}
    backgroundColor="$color6"
    borderRadius={1000_000_000}
    paddingHorizontal="$2"
    paddingVertical="$1"
    alignSelf="flex-start"
    {...rest}
  >
    <View
      width={10}
      height={10}
      backgroundColor="$color10"
      borderRadius={1000}
    />
    <Text color="$color10" fontWeight="$4">
      {status}
    </Text>
  </View>
);

const ActionView = ({
  hub,
  onHubAssignCustomer,
  onHubDelete,
  onHubEdit,
  onHubUpdateFirmware,
}: {
  hub: Hub;
  onHubAssignCustomer: (hub: Hub) => void;
  onHubDelete: (hub: Hub) => void;
  onHubEdit: (hub: Hub) => void;
  onHubUpdateFirmware: (hub: Hub) => void;
}) => {
  const { permissions } = useUserRole();
  const { t } = useTranslation();

  return (
    // Wraps rather than overflowing the column: below ~1800px the Action
    // column is narrower than four buttons and the last one was clipped.
    <View
      flexDirection="row"
      flexWrap="wrap"
      gap="$1.5"
      onPress={(e) => e.stopPropagation()}
    >
      <Button
        size="$3"
        disabled={!permissions.hub.update}
        icon={<UserPlus size="$1" />}
        {...iconLabel(t("a11y.assignCustomer"))}
        onPress={(e) => {
          e.stopPropagation();
          onHubAssignCustomer(hub);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
      <Button
        size="$3"
        disabled={!permissions.hub.update}
        icon={<RefreshCw size="$1" />}
        {...iconLabel(t("a11y.updateFirmware"))}
        onPress={(e) => {
          e.stopPropagation();
          onHubUpdateFirmware(hub);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
      <Button
        size="$3"
        disabled={!permissions.hub.update}
        icon={<Pencil size="$1" />}
        {...iconLabel(t("a11y.edit"))}
        onPress={(e) => {
          e.stopPropagation();
          onHubEdit(hub);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
      <Button
        size="$3"
        disabled={!permissions.hub.delete}
        icon={<Trash size="$1" />}
        {...iconLabel(t("a11y.delete"))}
        onPress={(e) => {
          e.stopPropagation();
          onHubDelete(hub);
        }}
        disabledStyle={{
          cursor: "default",
          opacity: 0.5,
        }}
      ></Button>
    </View>
  );
};

// Migration health + version currency rolled into one fleet-level signal. A
// hub can be ✗ on the per-version icons (firmware behind latest) but still
// Healthy here if all its migrations applied cleanly; conversely it can be ✓
// on versions but show "Migration failed" if a post-check failed. The two are
// independent — see the column tooltip for the operator-facing explanation.
type UpdatePillKind =
  | "healthy"
  | "update_available"
  | "pending"
  | "failed"
  | "install_failed"
  | "installing"
  | "unknown";

interface UpdatePillState {
  kind: UpdatePillKind;
  failureCount: number;
  pendingCount: number;
}

// Rendered through StatusIndicator so the update pill is the same size as the
// Status column's pill beside it.
const PILL_THEME: Record<UpdatePillKind, string> = {
  healthy: "green",
  update_available: "blue",
  pending: "yellow",
  failed: "red",
  install_failed: "red",
  installing: "blue",
  unknown: "gray",
};

// `migrations` lives under Hub.config alongside firmware / webHub / Water /
// HVAC / etc. Older hubs don't publish it yet, so null is a valid state and
// renders as "Unknown" rather than as a failure.
const getMigrationStatus = (
  config: string | null | undefined,
): HubMigrationsConfig | null => {
  if (!config) return null;
  try {
    const parsed = JSON.parse(config);
    if (parsed?.migrations && Array.isArray(parsed.migrations.available)) {
      return parsed.migrations as HubMigrationsConfig;
    }
  } catch {
    // malformed config — treat as no migration data
  }
  return null;
};

// `installStatus` lives under Hub.config next to migrations, but its writer
// is update-firmware-deb.sh on the hub (via processHubInstallStatus) — the
// only reporter that survives an install that bricks the firmware itself.
const getInstallStatus = (
  config: string | null | undefined,
): HubInstallStatusConfig | null => {
  if (!config) return null;
  try {
    const parsed = JSON.parse(config);
    if (parsed?.installStatus?.status) {
      return parsed.installStatus as HubInstallStatusConfig;
    }
  } catch {
    // malformed config — treat as no install-status data
  }
  return null;
};

// An install that dies outright — the exact case the install-status pill was
// added for — never sends a terminal report, so its last phase marker sits in
// config forever. Past this age the bar gives up and the row falls back to the
// ordinary pill, which is what an operator needs to see.
const PROGRESS_STALE_MS = 15 * 60 * 1000;

const getInstallProgress = (
  config: string | null | undefined,
): HubInstallProgressConfig | null => {
  if (!config) return null;
  try {
    const parsed = JSON.parse(config);
    if (parsed?.installProgress?.steps > 0) {
      return parsed.installProgress as HubInstallProgressConfig;
    }
  } catch {
    // malformed config — treat as no progress data
  }
  return null;
};

// Progress only wins the cell while it's the newest word on this hub. The
// timestamp comparison matters because the two OTA targets share the slot:
// firmware's terminal report deletes only its own bar, so a web-hub bar left
// by the parallel install must not outrank a firmware failure reported after.
const getActiveProgress = (
  progress: HubInstallProgressConfig | null,
  installStatus: HubInstallStatusConfig | null,
): HubInstallProgressConfig | null => {
  if (!progress) return null;
  const at = Date.parse(progress.updatedAt);
  if (!Number.isFinite(at)) return null;
  if (Date.now() - at > PROGRESS_STALE_MS) return null;
  if (installStatus && Date.parse(installStatus.updatedAt) >= at) return null;
  return progress;
};

// Takes the place of the version row being installed, so an updating row is
// no taller than an idle one. The step detail lives in the details dialog.
const InstallProgressBar = ({
  progress,
  label,
  version,
  t,
}: {
  progress: HubInstallProgressConfig;
  label: string;
  version?: string;
  t: TFunction;
}) => {
  const pct = Math.min(100, Math.max(0, (progress.step / progress.steps) * 100));
  return (
    <YStack
      gap="$1.5"
      theme={PILL_THEME.installing as never}
      backgroundColor="$color3"
      borderRadius="$4"
      paddingHorizontal="$2"
      paddingVertical="$1.5"
      // Pulls the label back into line with the version rows around it.
      marginHorizontal="-$2"
    >
      <XStack alignItems="center" gap="$1.5">
        <Text fontSize="$1" color="$color11" width={32}>
          {label}
        </Text>
        <Text color="$color12" flex={1} numberOfLines={1}>
          {version ?? "—"}
        </Text>
        <Spinner size="small" color="$color10" />
        <Text color="$color11" fontSize="$2">
          {t("baseStations.updateStatus.installStep", {
            step: progress.step,
            steps: progress.steps,
          })}
        </Text>
      </XStack>
      <View
        role="progressbar"
        aria-label={t(
          progress.target === "web-hub"
            ? "baseStations.updateStatus.installingWebHub"
            : "baseStations.updateStatus.installingFirmware",
        )}
        aria-valuemin={0}
        aria-valuemax={progress.steps}
        aria-valuenow={progress.step}
        height={6}
        backgroundColor="$color5"
        borderRadius={1000}
        overflow="hidden"
      >
        <View
          height="100%"
          width={`${pct}%`}
          backgroundColor="$color10"
          borderRadius={1000}
        />
      </View>
    </YStack>
  );
};

// Priority: install_failed > failed > pending > update_available > healthy >
// unknown. "Pending" only fires when a migration is in `available` but absent
// from `state` entirely — failures are already accounted for in the state
// list and belong under `failed`, not `pending`.
const getUpdatePill = (
  fw: { version: string } | null,
  wh: { version: string } | null,
  migrations: HubMigrationsConfig | null,
  installStatus: HubInstallStatusConfig | null,
  latest: LatestVersions | null,
): UpdatePillState => {
  // installStatus always reflects the LAST install attempt (each attempt
  // overwrites it), so a lingering `failed` means the hub's firmware install
  // is genuinely broken — the stale-Healthy trap this feature exists to
  // close. A later attempt reporting success/rolled_back clears it.
  if (installStatus?.status === "failed") {
    return { kind: "install_failed", failureCount: 0, pendingCount: 0 };
  }
  if (!migrations) {
    return { kind: "unknown", failureCount: 0, pendingCount: 0 };
  }
  const failureCount = migrations.state.filter(
    (e) => e.status === "failed",
  ).length;
  const reportedNames = new Set(migrations.state.map((e) => e.name));
  const pendingCount = migrations.available.filter(
    (n) => !reportedNames.has(n),
  ).length;
  if (failureCount > 0) {
    return { kind: "failed", failureCount, pendingCount };
  }
  if (pendingCount > 0) {
    return { kind: "pending", failureCount: 0, pendingCount };
  }
  const fwBehind = Boolean(
    fw?.version && latest?.firmware?.version && fw.version !== latest.firmware.version,
  );
  const whBehind = Boolean(
    wh?.version && latest?.webHub?.version && wh.version !== latest.webHub.version,
  );
  if (fwBehind || whBehind) {
    return { kind: "update_available", failureCount: 0, pendingCount: 0 };
  }
  return { kind: "healthy", failureCount: 0, pendingCount: 0 };
};

const pillLabel = (pill: UpdatePillState, t: TFunction): string => {
  switch (pill.kind) {
    case "healthy":
      return t("baseStations.updateStatus.healthy");
    case "update_available":
      return t("baseStations.updateStatus.updateAvailable");
    case "pending":
      return t("baseStations.updateStatus.pending");
    case "failed":
      return pill.failureCount > 1
        ? t("baseStations.updateStatus.failedPlural", {
            count: pill.failureCount,
          })
        : t("baseStations.updateStatus.failedSingular");
    case "install_failed":
      return t("baseStations.updateStatus.installFailed");
    case "unknown":
    default:
      return t("baseStations.updateStatus.unknown");
  }
};

// Same JSON-blob parser as the firmware/webHub version sections — kept inline
// (not extracted) so the cell stays self-contained and there's nothing extra
// to import in callers that just want a single key.
const getVersionInfo = (
  config: string | null | undefined,
  key: "firmware" | "webHub" | "bsecProcessor" | "zpc",
): { version: string; updatedAt?: string; pinned?: string | null } | null => {
  if (!config) return null;
  try {
    const parsed = JSON.parse(config);
    // A pin with no version = the component isn't installed; keep it so the row can say so.
    if (parsed?.[key]?.version || parsed?.[key]?.pinned) {
      return {
        version: parsed[key].version ?? "",
        updatedAt: parsed[key].updatedAt,
        pinned: parsed[key].pinned,
      };
    }
  } catch {
    // malformed config — treat as no version reported
  }
  return null;
};

const formatTimestamp = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return formatDateTime(d);
};

/**
 * Epoch seconds (stored as a number, sometimes a string) -> display date.
 * Date only: the column sits mid-table between Customer and Serial Number,
 * and a full timestamp under each name wrapped every row onto three lines.
 * The hub's own Installation section still shows the time.
 */
const installedOn = (value?: number | string | null): string | null => {
  const seconds = typeof value === "string" ? parseInt(value, 10) : value;
  if (!seconds || Number.isNaN(seconds) || seconds <= 0) return null;
  return formatDate(new Date(seconds * 1000));
};

const VersionDetailRow = ({
  label,
  current,
  latest,
  pinned,
}: {
  label: string;
  current: string | undefined | null;
  latest?: string | null;
  // The version this hub's firmware pins; a different running version is flagged.
  pinned?: string | null;
}) => {
  const { t } = useTranslation();
  if (!current) {
    return (
      <XStack gap="$2" alignItems="center">
        <Text fontWeight="$4" width={160}>
          {label}
        </Text>
        {pinned ? (
          <Text fontSize="$1" color="$statusCritical">
            {t("baseStations.updateDetails.notInstalled", { pinned })}
          </Text>
        ) : (
          <Text color="$gray10">
            {t("baseStations.updateDetails.notReported")}
          </Text>
        )}
      </XStack>
    );
  }
  const onLatest = latest && current === latest;
  return (
    <XStack gap="$2" alignItems="center" flexWrap="wrap">
      <Text fontWeight="$4" width={160}>
        {label}
      </Text>
      <Text>{current}</Text>
      {pinned && pinned !== current ? (
        <Text fontSize="$1" color="$statusCritical">
          {t("baseStations.updateDetails.expectedSuffix", { pinned })}
        </Text>
      ) : null}
      {latest ? (
        onLatest ? (
          <Text fontSize="$1" color="$statusGood">
            {t("baseStations.updateDetails.latestSuffix")}
          </Text>
        ) : (
          <Text fontSize="$1" color="$statusCritical">
            {t("baseStations.updateDetails.behindSuffix", { latest })}
          </Text>
        )
      ) : null}
    </XStack>
  );
};

// Display-only variant that adds "pending" alongside applied/failed so the
// drawer can render a third row class for migrations that exist in the
// running firmware but haven't been recorded in /etc/homeguardian/applied-
// migrations yet.
type MigrationDisplayEntry =
  | HubMigrationEntry
  | { name: string; status: "pending"; at: null; error?: undefined };

const MigrationDetailRow = ({
  entry,
}: {
  entry: MigrationDisplayEntry;
}) => {
  const { t } = useTranslation();
  let icon: React.ReactNode;
  let primary: string;
  let secondary: string | null = null;

  if (entry.status === "applied") {
    icon = <Check size={14} color="$statusGood" />;
    const at = formatTimestamp(entry.at);
    primary = at
      ? t("baseStations.updateDetails.applied", { at })
      : t("baseStations.updateDetails.appliedNoTime");
    if (entry.error === "no post-check") {
      secondary = t("baseStations.updateDetails.noPostCheck");
    }
  } else if (entry.status === "failed") {
    icon = <X size={14} color="$statusCritical" />;
    const at = formatTimestamp(entry.at);
    primary = at
      ? t("baseStations.updateDetails.failed", { at })
      : t("baseStations.updateDetails.failedNoTime");
    secondary = entry.error ?? null;
  } else {
    icon = <Text color="$statusOffline">…</Text>;
    primary = t("baseStations.updateDetails.pending");
  }

  return (
    <XStack gap="$2" alignItems="flex-start">
      <View width={16} alignItems="center" paddingTop="$0.5">
        {icon}
      </View>
      <YStack flex={1}>
        <Text fontSize="$2">{entry.name}</Text>
        <Text fontSize="$1" color="$gray11">
          {primary}
        </Text>
        {secondary && (
          <Text fontSize="$1" color="$gray10">
            {secondary}
          </Text>
        )}
      </YStack>
    </XStack>
  );
};

const UpdateDetailsDialog = ({
  hub,
  latest,
  onClose,
}: {
  hub: Hub | null;
  latest: LatestVersions | null;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  if (!hub) return null;

  const fw = getVersionInfo(hub.config, "firmware");
  const wh = getVersionInfo(hub.config, "webHub");
  const zpc = getVersionInfo(hub.config, "zpc");
  const bsec = getVersionInfo(hub.config, "bsecProcessor");
  const migrations = getMigrationStatus(hub.config);
  const installStatus = getInstallStatus(hub.config);
  const activeProgress = getActiveProgress(
    getInstallProgress(hub.config),
    installStatus,
  );
  const reportedNames = new Set(migrations?.state.map((e) => e.name) ?? []);
  const pendingMigrations: MigrationDisplayEntry[] = (
    migrations?.available ?? []
  )
    .filter((n) => !reportedNames.has(n))
    .map((name) => ({ name, status: "pending" as const, at: null }));

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay backgroundColor="$black10" />
        <Dialog.Content
          width={560}
          maxWidth="95vw"
          maxHeight="90dvh"
          gap="$4"
          padding="$5"
          backgroundColor="$gray2"
          borderWidth={0}
        >
          <Dialog.Title
            size="$15"
            fontSize="$9.5"
            fontWeight="bold"
            // Size $15's 84px line height doubles when a phone wraps it.
            $sm={{ lineHeight: 36 }}
          >
            {t("baseStations.updateDetails.title", { name: hub.name })}
          </Dialog.Title>

          {/* The migration list outgrows a phone or a short laptop screen;
              only the body scrolls so the title and Close stay put. */}
          <ScrollView flexShrink={1} contentContainerStyle={{ gap: "$4" }}>
            <YStack gap="$3">
              <Text fontWeight="bold">
                {t("baseStations.updateDetails.versionsHeading")}
              </Text>
              <VersionDetailRow
                label={t("baseStations.updateDetails.firmware")}
                current={fw?.version}
                latest={latest?.firmware?.version ?? null}
              />
              {/* Screenless gateway: no web-hub is installed, so there is no
                  version to compare and nothing to be behind on. */}
              {!isGatewayHub(hub) && (
                <VersionDetailRow
                  label={t("baseStations.updateDetails.webHub")}
                  current={wh?.version}
                  latest={latest?.webHub?.version ?? null}
                />
              )}
              <VersionDetailRow
                label={t("baseStations.updateDetails.zpc")}
                current={zpc?.version}
                pinned={zpc?.pinned}
              />
              <VersionDetailRow
                label={t("baseStations.updateDetails.bsecProcessor")}
                current={bsec?.version}
                pinned={bsec?.pinned}
              />
              {activeProgress && (
                <YStack gap="$1">
                  <Text fontWeight="bold" color="$blue10">
                    {t("baseStations.updateDetails.installingHeading", {
                      target: activeProgress.target,
                    })}
                  </Text>
                  <Text fontSize="$2">
                    {t("baseStations.updateDetails.installStep", {
                      step: activeProgress.step,
                      steps: activeProgress.steps,
                      at:
                        formatTimestamp(
                          activeProgress.reportedAt ?? activeProgress.updatedAt,
                        ) ?? activeProgress.updatedAt,
                    })}
                  </Text>
                  {activeProgress.detail && (
                    <Text fontSize="$1" color="$gray10">
                      {activeProgress.detail}
                    </Text>
                  )}
                </YStack>
              )}
              {installStatus && installStatus.status !== "success" && (
                <YStack gap="$1">
                  <Text fontWeight="bold" color="$red10">
                    {installStatus.status === "rolled_back"
                      ? t("baseStations.updateDetails.rolledBackHeading")
                      : t("baseStations.updateDetails.installFailedHeading")}
                  </Text>
                  <Text fontSize="$2">
                    {t("baseStations.updateDetails.installAttempt", {
                      version: installStatus.version ?? "latest",
                      at:
                        formatTimestamp(
                          installStatus.reportedAt ?? installStatus.updatedAt,
                        ) ?? installStatus.updatedAt,
                    })}
                  </Text>
                  {installStatus.detail && (
                    <Text fontSize="$1" color="$gray10">
                      {installStatus.detail}
                    </Text>
                  )}
                </YStack>
              )}
            </YStack>

            <Separator />

            <YStack gap="$3">
              <Text fontWeight="bold">
                {t("baseStations.updateDetails.migrationsHeading")}
              </Text>
              {!migrations ? (
                <Text color="$gray10">
                  {t("baseStations.updateDetails.noMigrationData")}
                </Text>
              ) : (
                <YStack gap="$2.5">
                  {migrations.state.map((entry) => (
                    <MigrationDetailRow key={entry.name} entry={entry} />
                  ))}
                  {pendingMigrations.map((entry) => (
                    <MigrationDetailRow key={entry.name} entry={entry} />
                  ))}
                </YStack>
              )}
              {migrations?.updatedAt && (
                <Text fontSize="$1" color="$gray10">
                  {t("baseStations.updateDetails.checkedAt", {
                    at: formatTimestamp(migrations.updatedAt) ?? migrations.updatedAt,
                  })}
                </Text>
              )}
            </YStack>
          </ScrollView>

          <XStack justifyContent="flex-end">
            <Button onPress={onClose}>
              {t("baseStations.updateDetails.close")}
            </Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

// Records "this hub is going to customer X" before the homeowner ever signs
// in. The relation table is the only hub↔customer link, and `associateHub`
// reuses any existing relation's customerId, so the homeowner's own
// association inherits whatever is picked here. Each row toggles: a checked
// customer is assigned, clicking it again removes the attribution.
const AssignCustomerDialog = ({
  hub,
  customers,
  assignedIds,
  onClose,
  onToggle,
}: {
  hub: Hub | null;
  customers: Customer[];
  assignedIds: Set<string>;
  onClose: () => void;
  onToggle: (customerId: string, assigned: boolean) => Promise<void>;
}) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    setSearch("");
    setBusyId(null);
  }, [hub?.id]);

  if (!hub) return null;

  const needle = search.trim().toLowerCase();
  const matches = needle
    ? customers.filter((c) =>
        [c.name, c.email, c.companyName].some((field) =>
          (field ?? "").toLowerCase().includes(needle),
        ),
      )
    : customers;

  const handleToggle = async (customerId: string) => {
    if (busyId) return;
    setBusyId(customerId);
    try {
      await onToggle(customerId, assignedIds.has(customerId));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay backgroundColor="$black10" />
        <Dialog.Content
          width={480}
          maxWidth="95vw"
          maxHeight="90dvh"
          gap="$4"
          padding="$5"
          backgroundColor="$gray2"
          borderWidth={0}
        >
          <Dialog.Title
            size="$15"
            fontSize="$9.5"
            fontWeight="bold"
            $sm={{ lineHeight: 36 }}
          >
            {t("baseStations.assignCustomer.title", { name: hub.name })}
          </Dialog.Title>

          <Input
            value={search}
            onChangeText={setSearch}
            placeholder={t("baseStations.assignCustomer.searchPlaceholder")}
          />

          <ScrollView maxHeight={280} flexShrink={1}>
            <YStack gap="$1">
              {matches.length === 0 && (
                <Text color="$gray10">
                  {t("baseStations.assignCustomer.noMatches")}
                </Text>
              )}
              {matches.map((customer) => {
                const assigned = assignedIds.has(customer.id);
                return (
                  <XStack
                    key={customer.id}
                    padding="$2.5"
                    borderRadius="$3"
                    alignItems="center"
                    gap="$3"
                    opacity={busyId && busyId !== customer.id ? 0.6 : 1}
                    backgroundColor={assigned ? "$color4" : "transparent"}
                    hoverStyle={{
                      backgroundColor: "$color3",
                      cursor: busyId ? "wait" : "pointer",
                    }}
                    onPress={() => handleToggle(customer.id)}
                  >
                    <XStack
                      width={22}
                      height={22}
                      borderRadius="$2"
                      borderWidth={1}
                      borderColor={assigned ? "$statusGood" : "$gray8"}
                      backgroundColor={assigned ? "$statusGood" : "transparent"}
                      alignItems="center"
                      justifyContent="center"
                    >
                      {assigned && <Check size={14} color="white" />}
                    </XStack>
                    <YStack flex={1}>
                      <Text>
                        {customer.name ||
                          t("baseStations.assignCustomer.unnamed")}
                      </Text>
                      <Text fontSize="$1" color="$gray10">
                        {customer.email}
                      </Text>
                    </YStack>
                  </XStack>
                );
              })}
            </YStack>
          </ScrollView>

          <XStack gap="$3" justifyContent="flex-end">
            <Button onPress={onClose}>{t("common.close")}</Button>
          </XStack>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog>
  );
};

const BaseStations: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { sm, xs } = useMedia();
  const rowCounter = useRef(-1);
  const [isCreateDialogOpen, setCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isUpdateFirmwareDialogOpen, setUpdateFirmwareDialogOpen] =
    useState(false);
  const [isUnlinkGatewayDialogOpen, setUnlinkGatewayDialogOpen] =
    useState(false);
  const [selectedHub, setSelectedHub] = useState<Hub | null>(null);
  const [detailHub, setDetailHub] = useState<Hub | null>(null);
  const [assignHub, setAssignHub] = useState<Hub | null>(null);
  const { fetchHubs, addHub, modifyHub, removeHub, hubs, error, loading } =
    useHubs({ fetchAndSubscribeOnLoad: true });
  // A progress bar goes stale on a clock, not on an event: a hub that dies
  // mid-install sends nothing more, so without a tick its last phase marker
  // would keep rendering as a live bar until some unrelated push re-rendered
  // the row. Only runs while something is actually installing.
  const [, setProgressTick] = useState(0);
  const anyInstalling = useMemo(
    () => hubs.some((h) => getInstallProgress(h.config)),
    [hubs],
  );
  useEffect(() => {
    if (!anyInstalling) return undefined;
    const id = setInterval(() => setProgressTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [anyInstalling]);

  // The picker filters client-side, so it needs the whole customer base in one
  // page — the hook's default 60 would make customer 61 unsearchable.
  const { customers: allCustomers } = useCustomers(undefined, 500);
  // Treeline (the original agency) left test customers behind; they are
  // never real households, so keep them out of the assignment list.
  const customers = useMemo(
    () =>
      allCustomers.filter(
        (c) => !(c.email ?? "").toLowerCase().endsWith("@treelineinteractive.com"),
      ),
    [allCustomers],
  );
  const {
    homeownerOverviews,
    loading: homeownersLoading,
    refetch: refetchHomeowners,
  } = useHomeownersWithHubsListView();
  const { currentAuthenticatedUser } = useAuth();
  const { associateHubWithCustomer, dissociateHubFromCustomer } =
    useUserHubAssociation();
  const { showSuccessToast, showErrorToast } = useShowToast();
  const { triggerFirmwareUpdate } = useFirmwareUpdate();
  const { triggerWebHubUpdate } = useWebHubUpdate();
  const { unlink: unlinkGateway } = useGatewayLink();
  const { latest } = useLatestVersions();
  const { role, permissions } = useUserRole();
  const columnHelper = createColumnHelper<Hub>();

  // Hub → customer is only derivable by inverting the customer → hubs query;
  // the Hub table carries no customerId. Two customers on one hub is reachable
  // (associateHubCustomer only updates the caller's own relation row), so the
  // column flags it rather than silently picking one.
  const hubCustomers = useMemo(() => {
    const map = new Map<string, Customer[]>();
    homeownerOverviews.forEach(({ customer, hubs: customerHubs }) => {
      (customerHubs ?? []).forEach((hub: Hub) => {
        if (!hub?.id) return;
        const assigned = map.get(hub.id);
        if (assigned) {
          assigned.push(customer);
        } else {
          map.set(hub.id, [customer]);
        }
      });
    });
    return map;
  }, [homeownerOverviews]);

  const handleRowClick = (hubId: string) => {
    navigate(`/base-stations/${hubId}/sensors`);
  };

  const onHubDelete = (hub: Hub) => {
    setSelectedHub(hub);
    setDeleteDialogOpen(true);
  };

  const onHubEdit = (hub: Hub) => {
    setSelectedHub(hub);
    setCreateDialogOpen(true);
  };

  const onHubAdd = () => {
    setSelectedHub(null);
    setCreateDialogOpen(true);
  };

  const onHubUpdateFirmware = (hub: Hub) => {
    setSelectedHub(hub);
    setUpdateFirmwareDialogOpen(true);
  };

  const onHubUnlinkGateway = (hub: Hub) => {
    setSelectedHub(hub);
    setUnlinkGatewayDialogOpen(true);
  };

  const handleToggleCustomer = async (customerId: string, assigned: boolean) => {
    if (!assignHub) return;
    const hub = assignHub;
    const key = assigned ? "removeCustomer" : "assignCustomer";
    try {
      if (assigned) {
        await dissociateHubFromCustomer(hub.id, customerId);
      } else {
        // The relation is keyed by user, so the staff member doing the
        // assignment is the one recorded — admins get no alert notifications,
        // so no spam.
        const user = await currentAuthenticatedUser();
        await associateHubWithCustomer(hub.id, user.userId, customerId);
      }
      showSuccessToast(t(`baseStations.${key}.success`, { name: hub.name }));
      // Await so the dialog's checkmarks reflect the new state before the
      // row unlocks.
      await refetchHomeowners();
    } catch (err) {
      showErrorToast(
        t(`baseStations.${key}.error`, {
          message: err instanceof Error ? err.message : String(err),
        }),
      );
    }
  };

  const handleConfirmUnlinkGateway = async () => {
    if (!selectedHub) return;
    const hub = selectedHub;
    try {
      await unlinkGateway(hub.id);
      showSuccessToast(
        t("baseStations.unlinkGateway.success", { name: hub.name }),
      );
      // The row's parentHubId is what gates the unlink button, so the list has
      // to be refetched or the button lingers on an already-detached gateway.
      await fetchHubs();
    } catch (err) {
      showErrorToast(
        t("baseStations.unlinkGateway.error", {
          message: err instanceof Error ? err.message : String(err),
        }),
      );
    }
    setSelectedHub(null);
  };

  const handleConfirmFirmwareUpdate = async () => {
    if (!selectedHub) return;
    const hub = selectedHub;
    // A gateway is screenless and has no web-hub installed, so nothing on it
    // subscribes to `update-webhub/{hubId}`. Firing that endpoint anyway would
    // still resolve — the Lambda only publishes to MQTT and can't know whether
    // anyone is listening — and the toast would report a web-hub update that
    // never happened. Send firmware only and say so.
    const gateway = isGatewayHub(hub);

    // Fire both endpoints in parallel; report success only if both resolve.
    // settled-shape lets us tell the user which side failed if any.
    const results = await Promise.allSettled([
      triggerFirmwareUpdate({ thingName: hub.id }),
      ...(gateway ? [] : [triggerWebHubUpdate({ thingName: hub.id })]),
    ]);

    const firmwareResult = results[0];
    const webhubResult = results[1];
    const firmwareOk = firmwareResult?.status === "fulfilled";
    // A skipped web-hub update is not a failure — there was nothing to do.
    const webhubOk = gateway || webhubResult?.status === "fulfilled";

    const describeFailure = (
      result: PromiseSettledResult<unknown> | undefined,
    ): string => {
      if (!result || result.status !== "rejected") return "unknown error";
      return result.reason instanceof Error
        ? result.reason.message
        : String(result.reason);
    };

    if (firmwareOk && webhubOk) {
      showSuccessToast(
        gateway
          ? t("baseStations.updateHub.successGateway", { name: hub.name })
          : t("baseStations.updateHub.success", { name: hub.name }),
      );
    } else {
      const failures: string[] = [];
      if (!firmwareOk) {
        failures.push(`firmware (${describeFailure(firmwareResult)})`);
      }
      if (!webhubOk) {
        failures.push(`web-hub (${describeFailure(webhubResult)})`);
      }
      showErrorToast(
        t("baseStations.updateHub.error", { message: failures.join(", ") }),
      );
    }
    setSelectedHub(null);
  };

  const handleHubSubmit = async (values: CreateHubFormValues) => {
    let logoUrl = values.logoUrl;

    if (values.logoFile) {
      try {
        const hubId = selectedHub?.id || "new";
        logoUrl = await uploadToS3(values.logoFile, hubId);
      } catch (error) {
        console.error("Error uploading logo:", error);
      }
    }

    const payload = {
      name: values.name,
      serialNumber: values.serialNumber,
      macAddress: values.macAddress,
      status: values.status as HubStatus,
      logoUrl,
    };

    if (selectedHub) {
      await modifyHub({ id: selectedHub.id, ...payload });
    } else {
      await addHub(payload);
    }
    setSelectedHub(null);
  };

  const handleDeleteHubSubmit = async (hub: Hub) => {
    await removeHub(hub.id);
    setSelectedHub(null);
  };

  const transformStatusValue = (status: string): string => {
    switch (status.toUpperCase()) {
      case HubStatus.Active:
        return t("baseStations.table.statusValue.active");
      case HubStatus.Inactive:
        return t("baseStations.table.statusValue.inactive");
      case HubStatus.Maintenance:
        return t("baseStations.table.statusValue.maintenance");
      case HubStatus.Pending:
        return t("baseStations.table.statusValue.pending");
      default:
        return t("baseStations.table.statusValue.unknown");
    }
  };

  const columns = [
    columnHelper.accessor("name", {
      header: () => t("baseStations.table.name"),
      meta: { flex: 2.4, minWidth: 140 },
      // Gateways are badged: they behave differently under update (firmware
      // only) and report no web-hub version, so an unlabelled row reads as a
      // broken hub rather than a working gateway. Unlink sits with the badge
      // rather than in the action column — it only exists for linked
      // gateways, and a per-row extra button shoved the others out of line.
      cell: ({ row, getValue }) => (
        <XStack gap="$2" alignItems="center" flexWrap="wrap">
          <Text>{getValue()}</Text>
          {isGatewayHub(row.original) && (
            <XStack
              paddingHorizontal="$2"
              paddingVertical="$1"
              borderRadius="$2"
              backgroundColor="$blue4"
            >
              <Text fontSize="$1" color="$blue11" fontWeight="bold">
                {t("baseStations.table.gatewayBadge")}
              </Text>
            </XStack>
          )}
          {isGatewayHub(row.original) && row.original.parentHubId && (
            <Button
              size="$2"
              chromeless
              disabled={!permissions.hub.update}
              icon={<Unlink size={14} />}
              {...iconLabel(t("baseStations.unlinkGateway.title"))}
              $sm={{ minWidth: 32, minHeight: 32 }}
              onPress={(e: any) => {
                e?.stopPropagation?.();
                onHubUnlinkGateway(row.original);
              }}
              disabledStyle={{ cursor: "default", opacity: 0.5 }}
            />
          )}
        </XStack>
      ),
    }),
    columnHelper.accessor(
      (row) =>
        formatLocation({
          street: row.street,
          city: row.city,
          state: row.state,
          zip: row.zip,
        }) ?? "",
      {
      id: "location",
      header: () => t("baseStations.table.location"),
      meta: { flex: 2.4, minWidth: 130 },
      cell: ({ row }) => (
        <Text>
          {formatLocation({
            street: row.original.street,
            city: row.original.city,
            state: row.original.state,
            zip: row.original.zip,
          }) || t("baseStations.noLocation")}
        </Text>
      ),
      },
    ),
    columnHelper.accessor(
      (row) =>
        (hubCustomers.get(row.id) ?? [])
          .map((c) => c.name || c.email || "")
          .join(", "),
      {
      id: "customer",
      header: () => t("baseStations.table.customer"),
      meta: { flex: 1.3, minWidth: 110 },
      cell: ({ row }) => {
        // "Unassigned" is a claim about the data — don't make it while the
        // query that would populate it is still in flight.
        if (homeownersLoading) {
          return <Text color="$gray10">—</Text>;
        }
        const assigned = hubCustomers.get(row.original.id) ?? [];
        if (assigned.length === 0) {
          return (
            <Text color="$gray10">{t("baseStations.table.unassigned")}</Text>
          );
        }
        if (assigned.length > 1) {
          return (
            <Text color="$gray10">
              {t("baseStations.table.multipleCustomers")}
            </Text>
          );
        }
        const customer = assigned[0];
        return (
          <Text
            color="$hgPrimary"
            textDecorationLine="underline"
            cursor="pointer"
            onPress={(e: any) => {
              e?.stopPropagation?.();
              navigate(`/customers/${customer.id}`);
            }}
          >
            {customer.name || customer.email}
          </Text>
        );
      },
      },
    ),
    // Who signed off the first sensor install at this site, stamped from the
    // installer's own login when they finished it in the app. A blank here is
    // a site nobody has signed off yet — not a missing name.
    columnHelper.accessor("installedByName", {
      header: () => t("baseStations.table.installer"),
      meta: { flex: 1.2, minWidth: 100 },
      cell: (info) => {
        const name = info.getValue();
        const when = installedOn(info.row.original.installedAt);
        if (!name) {
          // Em dash, matching the Customer column: a site nobody has signed
          // off is an empty cell, and spelling that out wrapped to two lines
          // and collided with the status pill.
          return <Text color="$gray10">—</Text>;
        }
        return (
          <YStack>
            <Text>{name}</Text>
            {when ? (
              <Text fontSize="$2" color="$gray10">
                {when}
              </Text>
            ) : null}
          </YStack>
        );
      },
    }),
    columnHelper.accessor("serialNumber", {
      header: () => t("baseStations.table.serialNumber"),
      meta: { flex: 1.4, minWidth: 160 },
      cell: (info) => <Text>{info.getValue()}</Text>,
    }),
    columnHelper.display({
      id: "version",
      meta: { flex: 1.9, minWidth: 150 },
      header: () => (
        <Text
          fontWeight="bold"
          cursor="help"
          title={t("baseStations.updateStatus.tooltip")}
          // Phone cards use this as a plain grey label like the others.
          $sm={{ fontWeight: "400", color: "$gray11" }}
        >
          {t("baseStations.table.version")}
        </Text>
      ),
      cell: ({ row }) => {
        const hub = row.original;
        const fw = getVersionInfo(hub.config, "firmware");
        const wh = getVersionInfo(hub.config, "webHub");
        const migrations = getMigrationStatus(hub.config);
        const installStatus = getInstallStatus(hub.config);
        const pill = getUpdatePill(fw, wh, migrations, installStatus, latest);
        const activeProgress = getActiveProgress(
          getInstallProgress(hub.config),
          installStatus,
        );

        // ✓ when the hub's version matches the latest CI-published build, ✗
        // when behind. No indicator when we don't know the latest (marker
        // missing) or the hub hasn't reported that artifact yet.
        const renderIndicator = (
          current?: string,
          latestVersion?: string | null,
        ) => {
          if (!current || !latestVersion) return null;
          return current === latestVersion ? (
            <Check size={12} color="$statusGood" />
          ) : (
            <X size={12} color="$statusCritical" />
          );
        };

        return (
          <YStack
            gap="$1.5"
            alignSelf="stretch"
            theme="alt1"
            cursor="pointer"
            onPress={(e: any) => {
              e?.stopPropagation?.();
              setDetailHub(hub);
            }}
          >
            {activeProgress?.target === "firmware" ? (
              <InstallProgressBar
                progress={activeProgress}
                label={t("baseStations.table.versionFirmware")}
                version={fw?.version}
                t={t}
              />
            ) : (
              <XStack gap="$1.5" alignItems="center">
                <Text fontSize="$1" color="$gray10" width={32}>
                  {t("baseStations.table.versionFirmware")}
                </Text>
                <Text>{fw?.version ?? "—"}</Text>
                {renderIndicator(fw?.version, latest?.firmware?.version)}
              </XStack>
            )}
            {/* Gateways have no web-hub install and never report a version,
                so the row would sit at "—" forever and read as a fault. */}
            {!isGatewayHub(hub) &&
              (activeProgress?.target === "web-hub" ? (
                <InstallProgressBar
                  progress={activeProgress}
                  label={t("baseStations.table.versionWebHub")}
                  version={wh?.version}
                  t={t}
                />
              ) : (
                <XStack gap="$1.5" alignItems="center">
                  <Text fontSize="$1" color="$gray10" width={32}>
                    {t("baseStations.table.versionWebHub")}
                  </Text>
                  <Text>{wh?.version ?? "—"}</Text>
                  {renderIndicator(wh?.version, latest?.webHub?.version)}
                </XStack>
              ))}
            {!activeProgress && (
              <StatusIndicator
                status={pillLabel(pill, t)}
                theme={PILL_THEME[pill.kind]}
                // Phone cards right-align values; a short pill ("Healthy")
                // would otherwise sit under the FW label, not the edge.
                $sm={{ alignSelf: "flex-end" }}
              />
            )}
          </YStack>
        );
      },
    }),
    columnHelper.accessor("status", {
      header: () => t("baseStations.table.status"),
      meta: { flex: 1, minWidth: 150 },
      // Operational order, not alphabetical: the hubs that need eyes first.
      sortingFn: (a, b) =>
        statusRank(a.original.status) - statusRank(b.original.status),
      // Two flags, one above the other: whether data flows (Hub.status, which
      // the filter and the Analytics links use), then the live connection.
      cell: (info) => {
        const presence = getHubPresence(info.row.original);
        return (
          <YStack gap="$1.5">
            <StatusIndicator status={transformStatusValue(info.getValue())} />
            {presence && (
              <StatusIndicator
                status={t(`baseStations.table.connection.${presence}`)}
                theme={presence === "connected" ? "green" : "red"}
              />
            )}
          </YStack>
        );
      },
    }),
    columnHelper.display({
      id: "actions",
      header: () => t("baseStations.table.action"),
      meta: { flex: 2.3, minWidth: 150 },
      cell: ({ row }) => (
        <ActionView
          hub={row.original}
          onHubAssignCustomer={setAssignHub}
          onHubDelete={onHubDelete}
          onHubEdit={onHubEdit}
          onHubUpdateFirmware={onHubUpdateFirmware}
        />
      ),
    }),
  ];

  const [sorting, setSorting] = useState<SortingState>([
    { id: "name", desc: false },
  ]);
  // /base-stations?status=INACTIVE deep-links a filter (the Analytics tiles use it).
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState<
    (typeof STATUS_FILTERS)[number]
  >(() => {
    const wanted = (searchParams.get("status") ?? "").toUpperCase();
    return (STATUS_FILTERS as readonly string[]).includes(wanted)
      ? (wanted as (typeof STATUS_FILTERS)[number])
      : "all";
  });

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: hubs?.length ?? 0 };
    (hubs ?? []).forEach((hub) => {
      const key = (hub.status ?? "").toUpperCase();
      counts[key] = (counts[key] ?? 0) + 1;
    });
    return counts;
  }, [hubs]);

  // "all" | "none" (no sign-off yet) | an installer's stamped name.
  const [installerFilter, setInstallerFilter] = useState<string>("all");

  const installerOptions = useMemo(() => {
    const names = new Set<string>();
    (hubs ?? []).forEach((hub) => {
      if (hub.installedByName) names.add(hub.installedByName);
    });
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [hubs]);

  const visibleHubs = useMemo(
    () =>
      hubs.filter((hub) => {
        const statusOk =
          statusFilter === "all" ||
          (hub.status ?? "").toUpperCase() === statusFilter;
        const installerOk =
          installerFilter === "all" ||
          (installerFilter === "none"
            ? !hub.installedByName
            : hub.installedByName === installerFilter);
        return statusOk && installerOk;
      }),
    [hubs, statusFilter, installerFilter],
  );

  const table = useReactTable({
    data: visibleHubs,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  useEffect(() => {
    setDocumentTitle({ hubName: selectedHub?.name });
  }, [selectedHub]);

  const renderContent = () => {
    if (loading) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center" space="$4">
          <Spinner size="large" />
          <Text>{t("baseStations.loading")}</Text>
        </YStack>
      );
    }

    if (error) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text color="$red10">{t("common.error")}</Text>
          <Text>{error.message}</Text>
          <Button marginTop="$4" onPress={fetchHubs}>
            {t("common.retry")}
          </Button>
        </YStack>
      );
    }

    if (!hubs || hubs.length === 0) {
      return (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Text>{t("baseStations.noBaseStations")}</Text>
          <Button marginTop="$4" onPress={fetchHubs}>
            {t("common.refresh")}
          </Button>
        </YStack>
      );
    }

    const headerGroups = table.getHeaderGroups();
    const tableRows = table.getRowModel().rows;
    const allRowsLength = tableRows.length + headerGroups.length;
    rowCounter.current = -1;

    if (sm || xs) {
      return (
        <TableCards
          table={table}
          titleColumn="name"
          onRowPress={(row) => handleRowClick(row.original.id)}
        />
      );
    }

    // Below 1281px the flex columns crush (serials and versions overrun
    // their cells), so the table scrolls sideways at a width where the
    // Maintenance pill, the widest unbreakable content, fits in Status.
    return (
      <ScrollView horizontal contentContainerStyle={{ width: "100%" }}>
        <Table
          borderWidth={0}
          width="100%"
          maxWidth="100%"
          gap="$3"
          cellHeight="$8"
          // Sum of the column floors: below it the table scrolls sideways
          // instead of letting cells collide (fits a 1440 window exactly).
          minWidth={1090}
        >
          <Table.Head>
            {headerGroups.map((headerGroup) => {
              rowCounter.current++;
              return (
                <Table.Row
                  rowLocation={
                    rowCounter.current === 0
                      ? "first"
                      : rowCounter.current === allRowsLength - 1
                        ? "last"
                        : "middle"
                  }
                  key={headerGroup.id}
                >
                  {headerGroup.headers.map((header) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    return (
                      <Table.HeaderCell
                        key={header.id}
                        borderWidth={0}
                        {...cellLayout(header.column)}
                      >
                        <XStack
                          alignItems="center"
                          gap="$1.5"
                          cursor={canSort ? "pointer" : undefined}
                          onPress={
                            canSort
                              ? header.column.getToggleSortingHandler()
                              : undefined
                          }
                        >
                          <Text fontWeight="bold">
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext(),
                                )}
                          </Text>
                          {canSort &&
                            (sorted === "asc" ? (
                              <ArrowUp size={14} />
                            ) : sorted === "desc" ? (
                              <ArrowDown size={14} />
                            ) : (
                              <ArrowUpDown size={14} color="$gray9" />
                            ))}
                        </XStack>
                      </Table.HeaderCell>
                    );
                  })}
                </Table.Row>
              );
            })}
          </Table.Head>
            <Table.Body>
              {tableRows.map((row) => {
                rowCounter.current++;
                return (
                  <Table.Row
                    hoverStyle={{
                      backgroundColor: "$color2",
                      cursor: "pointer",
                    }}
                    pressStyle={{
                      backgroundColor: "$color3",
                    }}
                    onPress={() => handleRowClick(row.original.id)}
                    rowLocation={
                      rowCounter.current === 0
                        ? "first"
                        : rowCounter.current === allRowsLength - 1
                          ? "last"
                          : "middle"
                    }
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <Table.Cell
                        key={cell.id}
                        borderWidth={0}
                        {...cellLayout(cell.column)}
                      >
                        {/* Version is left unwrapped so its install card can
                            stretch to the column instead of the text's width. */}
                        {cell.column.id === "status" ||
                        cell.column.id === "version" ? (
                          flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )
                        ) : (
                          <Text theme="alt1">
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Text>
                        )}
                      </Table.Cell>
                    ))}
                  </Table.Row>
                );
              })}
            </Table.Body>
        </Table>
      </ScrollView>
    );
  };

  return (
    <YStack flex={1}>
      <XStack justifyContent="space-between" flexWrap="wrap" gap="$3">
        <YStack space="$4">
          <Text fontSize="$9" fontWeight="bold">
            {t("baseStations.title")}
          </Text>
          <Text fontSize="$3" color="$gray11">
            {t("baseStations.description")}
          </Text>
        </YStack>
        {(role === UserRole.ADMIN || role === UserRole.INSTALLER) && (
          <Button
            onPress={onHubAdd}
            backgroundColor="$hgPrimary"
            size={"$4"}
            disabled={!permissions.hub.create}
          >
            {t("baseStations.addHub")}
          </Button>
        )}
      </XStack>
      <Separator marginTop="$4" />
      {!loading && !error && (hubs?.length ?? 0) > 0 && (
        <XStack alignItems="center" gap="$3" marginTop="$4" flexWrap="wrap">
          <Text fontSize="$4" fontWeight="700">
            {t("baseStations.filter.label")}
          </Text>
          {STATUS_FILTERS.map((value) => (
            <Button
              key={value}
              size="$3"
              borderRadius="$14"
              onPress={() => setStatusFilter(value)}
              borderWidth={statusFilter === value ? 2 : 0}
              borderColor={statusFilter === value ? "$gray10" : "$transparent"}
            >
              <Text fontSize="$3">
                {value === "all"
                  ? t("baseStations.filter.all")
                  : t(`baseStations.table.statusValue.${value.toLowerCase()}`)}
              </Text>
              <Text fontSize="$2" color="$gray10">
                {statusCounts[value] ?? 0}
              </Text>
            </Button>
          ))}

          {/* Installer is a free-text roster, not a fixed set of states, so it
              gets a select rather than a pill per name. */}
          {/* The extra gap would indent it once the row wraps (<=735px). */}
          <XStack alignItems="center" gap="$2" $gtSm={{ marginLeft: "$3" }}>
            <Text fontSize="$4" fontWeight="700">
              {t("baseStations.filter.installerLabel")}
            </Text>
            <Select
              value={installerFilter}
              onValueChange={setInstallerFilter}
              options={[
                { label: t("baseStations.filter.allInstallers"), value: "all" },
                { label: t("baseStations.filter.noInstaller"), value: "none" },
                ...installerOptions.map((name) => ({
                  label: name,
                  value: name,
                })),
              ]}
            />
          </XStack>
        </XStack>
      )}
      <Card flex={1} marginTop="$4" overflow="hidden">
        {renderContent()}
      </Card>

      <StationFormModal
        open={isCreateDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onSubmit={handleHubSubmit}
        station={selectedHub}
      ></StationFormModal>

      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => handleDeleteHubSubmit(selectedHub as Hub)}
        title={t(
          isGatewayHub(selectedHub)
            ? "baseStations.confirm.titleGateway"
            : "baseStations.confirm.title",
        )}
        description={t(
          isGatewayHub(selectedHub)
            ? "baseStations.confirm.descriptionGateway"
            : "baseStations.confirm.description",
        )}
      ></ConfirmDialog>

      <ConfirmDialog
        open={isUpdateFirmwareDialogOpen}
        onOpenChange={setUpdateFirmwareDialogOpen}
        onConfirm={handleConfirmFirmwareUpdate}
        title={t(
          isGatewayHub(selectedHub)
            ? "baseStations.updateHub.titleGateway"
            : "baseStations.updateHub.title",
        )}
        description={t(
          isGatewayHub(selectedHub)
            ? "baseStations.updateHub.descriptionGateway"
            : "baseStations.updateHub.description",
          { name: selectedHub?.name ?? "" },
        )}
      ></ConfirmDialog>

      <ConfirmDialog
        open={isUnlinkGatewayDialogOpen}
        onOpenChange={setUnlinkGatewayDialogOpen}
        onConfirm={handleConfirmUnlinkGateway}
        title={t("baseStations.unlinkGateway.title")}
        description={t("baseStations.unlinkGateway.description", {
          name: selectedHub?.name ?? "",
        })}
      ></ConfirmDialog>

      <UpdateDetailsDialog
        hub={detailHub}
        latest={latest}
        onClose={() => setDetailHub(null)}
      />

      <AssignCustomerDialog
        hub={assignHub}
        customers={customers}
        assignedIds={
          new Set(
            (assignHub ? hubCustomers.get(assignHub.id) ?? [] : []).map(
              (c) => c.id,
            ),
          )
        }
        onClose={() => setAssignHub(null)}
        onToggle={handleToggleCustomer}
      />

      <DemoLauncher hubs={hubs} hubCustomers={hubCustomers} />
    </YStack>
  );
};

export default BaseStations;
